import fuzzysort from 'fuzzysort';
import { defineStore } from 'pinia';
import { ref, shallowRef } from 'vue';
import { Get, type ListResponse } from '@/scripts/api';
import type { School } from '@sas/shared/database/pas';

/// Types
type IndexedSchool = Pick<
  School,
  'SCH_School_PK' | 'SCH_Name' | 'SCH_DisplayName' | 'SCH_City' | 'STA_Code' | 'SCH_Type'
>;

interface SearchableSchool extends IndexedSchool {
  Keywords: ReturnType<typeof fuzzysort.prepare>;
}

const SELECT = 'SCH_School_PK,SCH_Name,SCH_DisplayName,SCH_City,STA_Code,SCH_Type';
const STORAGE_KEY = 'sas:admin:schoolIndex';

function readStorage(): SearchableSchool[] | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const rows = JSON.parse(raw) as SearchableSchool[];
    // fuzzysort.Prepared doesn't survive JSON round-trips — reconstruct from the source fields.
    for (const r of rows) {
      r.Keywords = fuzzysort.prepare([r.SCH_DisplayName, r.SCH_Name].filter(Boolean).join(' '));
    }
    return rows;
  } catch {
    return null;
  }
}

function writeStorage(rows: SearchableSchool[]): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(rows));
  } catch {
    // Quota exceeded or storage disabled — non-fatal.
  }
}

/**
 * Session-scoped cache of every school the admin can navigate to, plus a fuzzy-ranked search over
 * SCH_DisplayName + SCH_Name (concatenated into one Keywords haystack so fuzzysort scores both with
 * prefix/word-boundary preference). Lazy-loaded — the first call to `Search()` (or
 * `EnsureLoaded()`) fetches the full list from the OData REST endpoint and walks `nextLink` cursors
 * until exhausted. Stale-on-rename is accepted: a school renamed in the same session shows the old
 * label until a full reload.
 */
export const useSchoolIndexStore = defineStore('schoolIndex', () => {
  /// Data
  const cached = readStorage();
  const schools = shallowRef<SearchableSchool[]>(cached ?? []);
  /**
   * True once the background fetch has completed at least once this session.
   */
  const loaded = ref(cached !== null);
  const loading = ref(false);
  let loadedPromise: Promise<void> | null = null;

  /// Methods
  async function fetchAllPages(): Promise<IndexedSchool[]> {
    const all: IndexedSchool[] = [];
    let after: string | null = null;
    do {
      const params = new URLSearchParams();
      params.set('$first', '1000');
      params.set('$select', SELECT);
      params.set('$orderby', 'SCH_Name');
      params.set('$filter', "SCH_Type ne 'sub'");
      if (after) params.set('$after', after);
      // Cursor pagination is inherently sequential — each page's $after token comes from the
      // previous response, so Promise.all is not an option here.
      // eslint-disable-next-line no-await-in-loop
      const res = (await Get('/School', params.toString())) as ListResponse<IndexedSchool> | null;
      all.push(...(res?.value ?? []));
      after = res?.nextLink
        ? new URL(res.nextLink, window.location.origin).searchParams.get('$after')
        : null;
    } while (after);
    return all;
  }

  /**
   * Fires the one-shot fetch + index build. Subsequent calls return the in-flight or settled
   * promise without re-fetching.
   */
  function EnsureLoaded(): Promise<void> {
    if (loadedPromise) return loadedPromise;
    loading.value = true;
    loadedPromise = (async () => {
      try {
        const rows = await fetchAllPages();
        for (const r of rows as SearchableSchool[]) {
          r.Keywords = fuzzysort.prepare([r.SCH_DisplayName, r.SCH_Name].filter(Boolean).join(' '));
        }
        schools.value = rows as SearchableSchool[];
        writeStorage(rows as SearchableSchool[]);
        loaded.value = true;
      } finally {
        loading.value = false;
      }
    })();
    return loadedPromise;
  }

  /**
   * Returns up to 10 schools ranked by fuzzy match quality. Empty term returns no results (not the
   * full list — header search shows a dropdown only after the user has typed). Triggers a lazy
   * fetch on first call so a subsequent keystroke gets real results.
   */
  function Search(term: string) {
    void EnsureLoaded();
    if (!schools.value.length || !term.trim()) return [];
    return [
      ...fuzzysort.go(term, schools.value, {
        key: 'Keywords',
        limit: 10,
        threshold: -10000,
      }),
    ];
  }

  return { schools, loaded, loading, EnsureLoaded, Search };
});
